import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/**
 * EAA-1814 (S3 rebuild): client for `GET /api/v1/dashboard` (EAA-1829,
 * portal-api commit 1ee1c74) — the single aggregate read that replaces the
 * three-separate-fetch S2 pass (`fetchOrganizations` + `fetchRoleMapSyncStatus`
 * + `fetchAdminApps`, each hit once per region by the old HomeKpiStrip /
 * HomeActionQueue / HomeAppsPending trio).
 *
 * Types below are transcribed field-for-field from `DashboardService`'s own
 * docstring (`services.py` lines 1587-1608) and `DashboardView` (`dashboard_views.py`)
 * in emg-ai-content-hub-be — NOT guessed. `attention[].type` is left as a
 * union-with-string-fallback: the four values the service emits today are
 * typed exactly, but a row of an unrecognised future `type` degrades to a
 * generic render rather than disappearing or throwing (see HomeActionQueue).
 *
 * NOTE (per ticket instruction — this client belongs in `src/api/*` long
 * term): this agent's ownership for EAA-1814 is `src/pages/home/**` only, so
 * the fetch is defined inline here rather than added to `src/api/console.ts`.
 * A follow-up should move `fetchDashboard`/these types into that file once
 * this page is the only caller left needing them (see the shared `request()`
 * helper pattern in `src/api/orgAdmin.ts` / `src/api/console.ts`).
 *
 * SCOPING (server-side, no FE param): `IsPortalAdmin`-gated. A super-admin
 * gets ecosystem-wide figures; an org-admin gets figures scoped to
 * `admin_org_ids` automatically — the FE never sends an org filter for this
 * endpoint. A plain user (zero admin grants) gets a 403, so this hook must
 * only be enabled when the caller has at least one `console.*` capability
 * (see `DASHBOARD_GATE_CAPABILITIES` below, used by `LauncherPage.tsx`).
 *
 * NOT PRESENT in the response (confirmed by reading both files — do not
 * invent): no date-range parameter, no per-app role-coverage breakdown, no
 * day-by-day account-provisioning series, and no month-over-month delta for
 * any KPI. Every region below that would need one of those renders an
 * explicit "unavailable" state instead.
 */

export interface DashboardKpis {
    organizations: { total: number };
    applications: { enabled: number; total: number };
    provisioning: { synced: number; pending: number; error: number };
    accounts: { total: number };
}

/** The four types `DashboardService._attention()` emits today, in the exact
 *  order it appends them (role map → assignment sync → app approval →
 *  orphan Level) — the "fixed order" HomeActionQueue renders in. */
export type DashboardAttentionType =
    | 'role_map_paused'
    | 'assignment_sync_error'
    | 'app_pending_approval'
    | 'orphan_level';

export interface DashboardAttentionItem {
    /** Typed as the four known values with a string fallback — a future
     *  fifth type from portal-api must render (generically), never vanish. */
    type: DashboardAttentionType | (string & {});
    /** Backend's own (English, internal) label — HomeActionQueue does NOT
     *  display this; it builds Vietnamese copy per `type` per the glossary
     *  (COPY rules), falling back to this string only for an unknown type. */
    label: string;
    count: number;
    target: string;
}

export interface DashboardActivityRow {
    id: string;
    source: 'account' | 'admin';
    action: string;
    actor: string;
    target: string;
    success: boolean;
    /** ISO 8601, already `.isoformat()`-ed server-side. */
    created_at: string;
}

export interface DashboardData {
    kpis: DashboardKpis;
    attention: DashboardAttentionItem[];
    recent_activity: DashboardActivityRow[];
}

/**
 * Every capability that gates at least one dashboard region below (KPI
 * tiles, the action queue, the two unavailable-data cards, recent activity).
 * `LauncherPage` uses `.some(can)` over this list to decide whether the
 * caller is a portal admin of ANY kind at all — i.e. whether it is worth
 * attempting `GET /dashboard` in the first place (a plain user with none of
 * these gets a guaranteed 403, so the fetch is skipped rather than firing
 * and immediately erroring). There is still no single `console.home.*`
 * capability — each region re-checks its OWN specific entry from this list.
 */
export const DASHBOARD_GATE_CAPABILITIES = [
    'console.orgs.view',
    'console.accounts.issue',
    'console.apps.view',
    'console.rolemap.view',
    'console.audit.view',
] as const;

interface ApiEnvelope<T> {
    success: boolean;
    code: number;
    message?: string;
    description?: string;
    data: T;
}

/** Thrown on 401/403 so the caller can decide whether to re-login (401) or
 *  just treat it as "not a portal admin" (403, e.g. grants revoked mid-session —
 *  the view's own fail-closed branch) rather than an error banner. */
export class DashboardAccessError extends Error {
    readonly status: number;
    constructor(status: number) {
        super(status === 401 ? 'Unauthorized' : 'Forbidden');
        this.name = 'DashboardAccessError';
        this.status = status;
    }
}

export async function fetchDashboard(apiBase: string, token: string): Promise<DashboardData> {
    const res = await fetch(`${apiBase}/api/v1/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401 || res.status === 403) {
        throw new DashboardAccessError(res.status);
    }
    if (!res.ok) {
        throw new Error(`GET /dashboard failed with status ${res.status}`);
    }
    const envelope = (await res.json()) as ApiEnvelope<DashboardData>;
    return envelope.data;
}

/**
 * One shared query for every dashboard region (KPI row, action queue, the two
 * unavailable-data cards, recent activity) — `queryKey` is stable across all
 * of them so mounting several regions in the same page costs exactly one
 * network request, matching the old per-source sharing convention.
 */
export function useDashboardQuery(
    apiBase: string,
    accessToken: string | null,
    enabled: boolean
): UseQueryResult<DashboardData, Error> {
    return useQuery({
        queryKey: ['dashboard', accessToken],
        queryFn: () => fetchDashboard(apiBase, accessToken as string),
        enabled: enabled && Boolean(accessToken),
        retry: false,
    });
}
